import { useState } from 'react';
import { operatorConsole, type SecurityState } from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';

/**
 * One card, two layers. `scope: 'personal'` edits the addresses this person
 * signs in from — everyone has that. `scope: 'operator'` edits the perimeter
 * for the whole console and belongs to the owner.
 *
 * Both lists apply together, so an empty one means "no restriction from this
 * side" rather than "block everything".
 */
export function IpWhitelistCard({
  scope,
  currentIp,
  whitelist,
  onSaved,
}: {
  scope: 'personal' | 'operator';
  currentIp: string;
  whitelist: string[];
  onSaved: (state: SecurityState) => void;
}) {
  const personal = scope === 'personal';
  const [text, setText] = useState(whitelist.join('\n'));
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fieldId = `sec-ip-${scope}`;

  async function handleSave() {
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const next = personal
        ? await operatorConsole.updatePersonalIpWhitelist(text, password)
        : await operatorConsole.updateIpWhitelist(text, password);
      setPassword('');
      setSuccess('IP allowlist saved.');
      onSaved(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-semibold">
          {personal ? 'Your sign-in IPs' : 'Console IP allowlist (whole team)'}
        </h2>
        <p className="text-sm text-[var(--co-muted)]">
          {personal ? (
            <>
              Restrict <strong>your own</strong> console access to these IPs (or CIDR ranges).
              You're connecting from <code>{currentIp}</code> — keep it covered or you'll lock
              yourself out. Leave it empty to sign in from anywhere your operator allows.
            </>
          ) : (
            <>
              Applies to everyone on this console, on top of each person's own list. You're
              connecting from <code>{currentIp}</code> — keep it covered or you'll lock yourself
              out. To remove all restrictions, contact support.
            </>
          )}
        </p>
      </header>

      <FormField label="Allowed IPs (one per line or comma-separated)" htmlFor={fieldId}>
        <textarea
          id={fieldId}
          rows={5}
          className="w-full rounded-md border border-[var(--co-border)] bg-[var(--co-bg)] p-2 font-mono text-sm"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </FormField>

      <FormField label="Current password" htmlFor={`${fieldId}-pw`} error={error ?? undefined}>
        <Input
          id={`${fieldId}-pw`}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </FormField>

      {success && (
        <p className="text-sm text-[var(--co-success)]" role="status">
          {success}
        </p>
      )}

      <div>
        <Button type="button" onClick={() => void handleSave()} disabled={saving || password === ''}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </section>
  );
}
